// EDEN · minimal DOM helpers.

/** HTML-escape untrusted text before interpolating into template strings. */
export function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function appRoot(): HTMLElement {
  const el = document.getElementById("app");
  if (!el) throw new Error("#app not found");
  return el;
}

/**
 * Block paste into a field so its text is the teacher's own English (ModuleD spec §2, §4.4).
 * Covers keyboard (Ctrl/Cmd+V) and context-menu paste (the `paste` event) plus drag-drop
 * (the `drop` event). Not a security control — the point is an honest written trajectory.
 */
export function lockPaste(el: HTMLElement | null): void {
  if (!el) return;
  // Say so, calmly — a paste that silently does nothing reads as "the app is broken" (2026-07-27
  // click-through, defect 2).
  const say = (): void => notify("Paste is off in this box — please type it in your own words.");
  el.addEventListener("paste", (e) => { e.preventDefault(); say(); });
  el.addEventListener("drop", (e) => { e.preventDefault(); say(); });
  el.addEventListener("dragover", (e) => e.preventDefault());
}

/** A quiet message at the bottom of the screen for a moment (calm confirmations only). */
export function notify(message: string): void {
  let el = document.getElementById("eden-toast");
  if (!el) {
    el = document.createElement("div");
    el.id = "eden-toast";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add("show");
  const box = el as HTMLElement & { _t?: number };
  window.clearTimeout(box._t);
  box._t = window.setTimeout(() => el!.classList.remove("show"), 2800);
}

/** Textareas marked .grow fit their text — no inner scrollbar hiding what the teacher wrote. Sized
 *  now and on every keystroke. Safe to call again (a box is wired once). Needs the boxes to be in
 *  the document to measure, so the router calls it right after a page is mounted. */
export function autoGrow(root: HTMLElement): void {
  root.querySelectorAll<HTMLTextAreaElement>("textarea.grow").forEach((el) => {
    const fit = (): void => {
      if (!el.isConnected) return;
      el.style.height = "auto";
      el.style.height = `${Math.max(el.scrollHeight + 2, 72)}px`;
    };
    fit();
    if (el.dataset.growWired) return;
    el.dataset.growWired = "1";
    el.addEventListener("input", fit);
  });
}
